class HttpError extends Error {
    constructor(message, status) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
    }
}

class ApiExceptionError extends Error {
    constructor(message, status, userFriendlyExceptionMessage) {
        super(message);
        this.name = 'ApiExceptionError';
        this.status = status;
        this.userFriendlyExceptionMessage = userFriendlyExceptionMessage;
    }
}

class TimeoutError extends Error {
    constructor(message, userFriendlyExceptionMessage) {
        super(message);
        this.name = 'TimeoutError';
        this.userFriendlyExceptionMessage = userFriendlyExceptionMessage;
    }
}

class HttpClient {
    _baseUrl;
    _antiForgeryToken;
    _timeoutErrorMessage;

    constructor(baseUrl, antiForgeryToken, timeoutErrorMessage) {
        this._baseUrl = baseUrl;
        this._antiForgeryToken = antiForgeryToken;
        this._timeoutErrorMessage = timeoutErrorMessage;
    }

    async get(endpoint, options = {}) {
        return this._handleRequest(endpoint, 'GET', null, options);
    }

    async post(endpoint, body = null, options = {}) {
        return this._handleRequest(endpoint, 'POST', body, options);
    }

    async _handleRequest(endpoint, method, body = null, options = {}) {
        const {
            timeout = 60000,
            headers = {},
            signal
        } = options;

        const controller = signal || new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(this._baseUrl + endpoint, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    '__RequestVerificationToken': this._antiForgeryToken,
                    ...headers
                },
                body: body !== null ? JSON.stringify(body) : null,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const error = await response.json();
                if (error.ErrorCode || error.UserFriendlyExceptionMessage) {
                    throw new ApiExceptionError(`Api Exception error!\nError Code: ${error.ErrorCode}\nMessage: ${error.UserFriendlyExceptionMessage}`, error.status, error.UserFriendlyExceptionMessage);
                } else {
                    throw new HttpError(`HTTP error! status: ${response.status}, message: ${await response.text()}`, response.status);
                }
            }

            const data = await response.json();

            if (!data) {
                throw new Error('No data found in response');
            }

            return data;
        } catch (error) {
            if (error.name === 'AbortError') {
                throw new TimeoutError('Request timed out', this._timeoutErrorMessage);
            } else if (error instanceof SyntaxError) {
                throw new Error('Invalid JSON in response');
            } else {
                throw error;
            }
        } finally {
            if (!signal) {
                clearTimeout(timeoutId);
            }
        }
    }
}

export default HttpClient;